// src/slices/systemConfiguration/thunk.ts
// Async thunks cho Cấu hình Hệ thống — gọi API thật system-service (SystemConfigurationController)

import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getSystemConfig,
  updateRegionConfig as updateRegionConfigAPI,
  updateLookupConfig as updateLookupConfigAPI,
  updateCommissionConfig as updateCommissionConfigAPI,
  updatePriceConfig as updatePriceConfigAPI,
  updateTaxConfig as updateTaxConfigAPI,
  updateCrossPrizePayoutConfig as updateCrossPrizePayoutConfigAPI,
  updatePrizeClaimValidityConfig as updatePrizeClaimValidityConfigAPI,
  validateBaseUrl,
  invalidateTicketSystemConfigCache,
} from 'helpers/fakebackend_helper';
import { CacheService } from 'services/cache';
import { CacheConfig } from 'services/cache/CacheConfig';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('system_configuration_messages');

// Write-through: ghi đè lms_cache_system_config ngay sau khi update thành công, không đợi TTL hết hạn
const writeSystemConfigCache = (config: unknown): void => {
  CacheService.set(CacheConfig.SYSTEM_CONFIG.key, config, CacheConfig.SYSTEM_CONFIG.ttl);
};

// Xóa cache Redis "system-configuration" bên ticket-service (LookupBaseUrl dùng khi sinh QR) —
// best-effort: lỗi ở đây không được làm fail luồng lưu cấu hình chính (config đã lưu đúng ở
// system-service rồi), cache bên ticket-service tự hết hạn sau 30 phút nếu lời gọi này thất bại.
const invalidateTicketServiceCache = (): void => {
  invalidateTicketSystemConfigCache().catch(() => undefined);
};

// Lấy cấu hình hệ thống hiện tại (singleton — chỉ có 1 bản ghi)
export const fetchSystemConfig = createAsyncThunk(
  'SystemConfiguration/fetchConfig',
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await getSystemConfig();
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || error?.message || st('co_loi_xay_ra_khi_tai_message')
      );
    }
  }
);

// Cập nhật cấu hình vùng miền/loại vé
export const updateRegionConfig = createAsyncThunk(
  'SystemConfiguration/updateRegionConfig',
  async (data: {
    region: string;
    ticketTypeId: number | null;
    digitCount: number;
    drawRounds: number;
    quantityPerPeriod: number;
    requireSpecialSymbol: boolean;
    defaultPrizePayoutCommissionRate: number;
    pregeneratedSymbols: string;
    // Mục 8bis (README §8) — gộp 3 tab cũ vào form Profile Vùng miền.
    defaultCommissionRate: number;
    defaultTicketPrice: number;
    taxThreshold: number;
    taxRate: number;
    prizeClaimPersonalInfoThreshold: number;
  }, { rejectWithValue }) => {
    try {
      const response = await updateRegionConfigAPI(data);
      const config = response.data || response;
      writeSystemConfigCache(config);
      invalidateTicketServiceCache();
      return config;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || error?.message || st('co_loi_xay_ra_khi_luu_message')
      );
    }
  }
);

// Cập nhật cấu hình đường dẫn tra cứu (lookup)
export const updateLookupConfig = createAsyncThunk(
  'SystemConfiguration/updateLookupConfig',
  async (data: {
    lookupBaseUrl: string;
    returnTicketStartTime: string;
    returnTicketEndTime: string;
  }, { rejectWithValue }) => {
    try {
      const response = await updateLookupConfigAPI(data);
      const config = response.data || response;
      writeSystemConfigCache(config);
      invalidateTicketServiceCache();
      return config;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || error?.message || st('co_loi_xay_ra_khi_luu_message_2')
      );
    }
  }
);

// Cập nhật hoa hồng trả thưởng mặc định (chỉ dùng prefill khi tạo đại lý mới — mục 13.4)
export const updateCommissionConfig = createAsyncThunk(
  'SystemConfiguration/updateCommissionConfig',
  async (data: { defaultCommissionRate: number }, { rejectWithValue }) => {
    try {
      const response = await updateCommissionConfigAPI(data);
      const config = response.data || response;
      writeSystemConfigCache(config);
      return config;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || error?.message || st('co_loi_xay_ra_khi_luu_message_3')
      );
    }
  }
);

// Cập nhật giá vé mặc định (chỉ dùng prefill khi lập phiếu giao vé cho đại lý — mục 8.g, README_UPDATE.md)
export const updatePriceConfig = createAsyncThunk(
  'SystemConfiguration/updatePriceConfig',
  async (data: { defaultTicketPrice: number }, { rejectWithValue }) => {
    try {
      const response = await updatePriceConfigAPI(data);
      const config = response.data || response;
      writeSystemConfigCache(config);
      return config;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || error?.message || st('co_loi_xay_ra_khi_luu_message_4')
      );
    }
  }
);

// Cập nhật ngưỡng + tỷ lệ khấu trừ Thuế TNCN (Thẩm định Hồ sơ)
export const updateTaxConfig = createAsyncThunk(
  'SystemConfiguration/updateTaxConfig',
  async (
    data: { taxThreshold: number; taxRate: number; prizeClaimPersonalInfoThreshold: number },
    { rejectWithValue },
  ) => {
    try {
      const response = await updateTaxConfigAPI(data);
      const config = response.data || response;
      writeSystemConfigCache(config);
      return config;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || error?.message || st('co_loi_xay_ra_khi_luu_message_5')
      );
    }
  }
);

// Mục 8.2, README_UPDATE.md — bật/tắt "đại lý trả thưởng chéo" (toàn hệ thống, hiệu lực ngay).
// Toggle lưu ngay khi người dùng gạt công tắc — không gộp vào form Profile Vùng miền vì form đó
// có hiệu lực "kỳ tiếp theo", còn cờ này phải có tác dụng ngay với việc lập hồ sơ trả thưởng.
export const updateCrossPrizePayoutConfig = createAsyncThunk(
  'SystemConfiguration/updateCrossPrizePayoutConfig',
  async (data: { allowAgentCrossPrizePayout: boolean }, { rejectWithValue }) => {
    try {
      const response = await updateCrossPrizePayoutConfigAPI(data);
      const config = response.data || response;
      writeSystemConfigCache(config);
      return config;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || error?.message || st('co_loi_xay_ra_khi_luu_message_6')
      );
    }
  }
);

// Thời hạn trả thưởng hợp lệ (ngày, tính từ ngày mở thưởng in trên vé) — lưu riêng, hiệu lực ngay
// cho việc lập hồ sơ trả thưởng (payout-service đọc qua GET SystemConfiguration/tax).
export const updatePrizeClaimValidityConfig = createAsyncThunk(
  'SystemConfiguration/updatePrizeClaimValidityConfig',
  async (data: { prizeClaimValidDays: number }, { rejectWithValue }) => {
    try {
      const response = await updatePrizeClaimValidityConfigAPI(data);
      const config = response.data || response;
      writeSystemConfigCache(config);
      return config;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || error?.message || st('co_loi_xay_ra_khi_luu_thoi_han_tra_thuong_message')
      );
    }
  }
);

// Kiểm tra khả năng truy cập của Base URL trước khi lưu (không persist ở backend)
export const testLookupUrl = createAsyncThunk(
  'SystemConfiguration/testLookupUrl',
  async (url: string, { rejectWithValue }) => {
    try {
      const response = await validateBaseUrl(url);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || error?.message || st('khong_the_kiem_tra_duong_dan_message')
      );
    }
  }
);
